/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { loadCustomMetricGroupsByInitiativeId, reloadCustomMetricGroupsByInitiativeId } from '../../actions/initiative';
import { loadInitiativeQuestions, reloadInitiativeQuestions } from '../../actions/blueprints';
import { unloadCustomMetricGroupSurveyId } from '../../slice/metricGroupsSlice';
import Loader from '../../components/loader';
import CustomMetricsComponent from '../../components/custom-metrics';
import { generateUrl } from '../util';
import CustomMetricContainer, { getBaseRoute } from '../../components/custom-metrics/CustomMetricContainer';
import { handleUnloadSurvey } from '../../actions/survey';
import queryString from 'query-string';
import { useAppSelector } from '../../reducers';
import { CustomMetricsViewMode } from '../../components/custom-metrics/constants';

export interface CustomMetricRouteParams {
  initiativeId?: string;
  groupId?: string;
  portfolioId?: string;
  view?: string;
}

const CustomMetrics = () => {

  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const match = useRouteMatch<{ initiativeId?: string, groupId?: string, portfolioId?: string, view?: string }>();
  const { groupId, view } = match.params;
  const initiativeId = match.params.portfolioId ?? match.params.initiativeId;
  const isPortfolioTracker = !!match.params.portfolioId;
  const query = queryString.parse(location.search);
  const surveyId = Array.isArray(query.surveyId) ? query.surveyId[0] : query.surveyId;

  const { data: metricGroups, loaded: metricGroupsLoaded, initiativeId: metricGroupsInitiativeId } = useAppSelector((state) => state.customMetricGroups);
  const { loaded: blueprintQuestionLoaded } = useAppSelector((state: any) => state.initiativeBlueprintQuestions);

  const isLoaded = metricGroupsLoaded && metricGroupsInitiativeId === initiativeId;

  const handleChangeMetricGroup = React.useCallback((updateParams: CustomMetricRouteParams) => {
    const params: { portfolioId?: string, initiativeId?: string, groupId?: string, view?: string } = {
      portfolioId: initiativeId,
      initiativeId: initiativeId,
    };
    if (updateParams.groupId) {
      params.groupId = updateParams.groupId;
    }
    if (updateParams.view) {
      params.view = updateParams.view;
    }

    const baseRoute = getBaseRoute(isPortfolioTracker);
    history.push({
      pathname: generateUrl(baseRoute, params),
      search: location.search
    });
  }, [initiativeId, location.search, isPortfolioTracker, history]);

  const handleReload = React.useCallback(async (firstRun = false) => {
    try {
      if (initiativeId) {
        if (blueprintQuestionLoaded) {
          if (firstRun && initiativeId === metricGroupsInitiativeId) {
            // Don't need to do this twice if it was loaded by another component
            return;
          }
          dispatch(handleUnloadSurvey())
          dispatch(reloadInitiativeQuestions(initiativeId));
          await dispatch(reloadCustomMetricGroupsByInitiativeId(initiativeId));
        } else {
          dispatch(handleUnloadSurvey())
          dispatch(loadInitiativeQuestions(initiativeId));
          await dispatch(loadCustomMetricGroupsByInitiativeId(initiativeId));
        }
        dispatch(unloadCustomMetricGroupSurveyId())
      }
    } catch (e) {
    }
  }, [initiativeId, blueprintQuestionLoaded, dispatch, metricGroupsInitiativeId])

  React.useEffect(() => {
    handleReload(true);
  }, [handleReload]);

  React.useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (groupId) {
      const found = metricGroups.find(g => g._id === groupId);
      if (found || groupId === CustomMetricsViewMode.Create || groupId === CustomMetricsViewMode.Manage) {
        return;
      }
    }

    // Go to custom metric dashboard when doesn't have groudId
    return handleChangeMetricGroup({ groupId: 'dashboard' });
  }, [groupId, initiativeId, metricGroups, handleChangeMetricGroup, isLoaded]);

  if (!isLoaded || !initiativeId) {
    return <Loader />;
  }

  return (
    <CustomMetricContainer surveyId={surveyId ?? undefined} groupId={groupId} view={view} initiativeId={initiativeId} isPortfolioTracker={isPortfolioTracker}>
      <CustomMetricsComponent
        handleReload={handleReload}
        handleChangeMetricGroup={handleChangeMetricGroup}
      />
    </CustomMetricContainer>
  );
}

export default CustomMetrics;
